/**
 * アバター(きせかえ)画面(docs/02_game_design.md §7.1 / §7.3)。
 *
 * - ベースキャラを絵文字から選ぶ
 * - 6スロットそれぞれに **保有アイテムだけ** を並べて装備を切り替える
 * - 装備の効果(ScoreModifier)は engine の `resolveModifiers` で合成して表示する。
 *   同じ値がそのままプレイの `TypingSession` に渡るので、表示と実効果は必ず一致する。
 */

import {
  BASE_CHARACTERS,
  SLOT_LABELS,
  describeEffect,
  ownedBySlot,
  summarizeModifiers,
} from '../logic/avatar';
import { ITEM_SLOTS } from '../services/catalog';
import { selectEquippedModifiers, useGameStore } from '../store/gameStore';
import { AvatarView } from './components/AvatarView';
import { ItemIcon } from './components/ItemIcon';

export function AvatarScreen() {
  const state = useGameStore();
  const owned = ownedBySlot(state.catalog, state.inventory);
  const effects = summarizeModifiers(selectEquippedModifiers(state));
  const totalOwned = Object.values(state.inventory.items).filter((count) => count > 0).length;

  return (
    <div className="screen avatar">
      <header className="stage-select-header">
        <button type="button" className="back-button" onClick={state.backFromMeta}>
          もどる
        </button>
        <h1 className="screen-title">きせかえ</h1>
        <p className="coin-line">
          <span className="coin-icon">◎</span> {state.progress.coins}
        </p>
      </header>

      <div className="avatar-body">
        <div className="avatar-preview">
          <AvatarView catalog={state.catalog} inventory={state.inventory} size="lg" />

          <section className="avatar-effects">
            <h2 className="section-title">そうびの こうか</h2>
            {effects.length > 0 ? (
              <ul>
                {effects.map((effect) => (
                  <li key={effect.type} className={`effect ${effect.type}`}>
                    {effect.text}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="hint-text">こうかの ある アイテムを そうびすると ここに でるよ</p>
            )}
          </section>

          <section className="base-picker">
            <h2 className="section-title">キャラ</h2>
            <div className="base-list">
              {BASE_CHARACTERS.map((base) => (
                <button
                  key={base.id}
                  type="button"
                  className={`base-card ${state.inventory.base === base.id ? 'on' : ''}`}
                  aria-pressed={state.inventory.base === base.id}
                  onClick={() => void state.setBaseCharacter(base.id)}
                >
                  <span className="base-emoji">{base.emoji}</span>
                  <span className="base-name">{base.name}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="slot-list">
          {totalOwned === 0 ? (
            <p className="hint-text big">
              まだ アイテムを もっていないよ。ガチャを ひいてみよう!
              <button type="button" className="big-button" onClick={() => void state.goGacha()}>
                🎁 ガチャへ
              </button>
            </p>
          ) : null}

          {ITEM_SLOTS.map((slot) => {
            const items = owned[slot];
            const equippedId = state.inventory.equipped[slot] ?? null;
            return (
              <section key={slot} className={`slot-section slot-${slot}`}>
                <h2 className="section-title">{SLOT_LABELS[slot]}</h2>
                <div className="item-grid">
                  <button
                    type="button"
                    className={`item-card none ${equippedId === null ? 'on' : ''}`}
                    aria-pressed={equippedId === null}
                    onClick={() => void state.equipItem(slot, null)}
                  >
                    <span className="item-emoji">✖</span>
                    <span className="item-name">なし</span>
                  </button>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`item-card rarity-${item.rarity} ${equippedId === item.id ? 'on' : ''}`}
                      aria-pressed={equippedId === item.id}
                      onClick={() => void state.equipItem(slot, item.id)}
                    >
                      <ItemIcon item={item} size="md" />
                      <span className="item-name">{item.name}</span>
                      <span className="item-rarity">{item.rarity}</span>
                      {item.effects.map((effect) => (
                        <span key={effect.type} className="item-effect">
                          {describeEffect(effect)}
                        </span>
                      ))}
                    </button>
                  ))}
                  {items.length === 0 ? <p className="hint-text">まだ もっていないよ</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
